import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import beInitReaderPayment from 'actions/pos/beInitReaderPayment.action';
import feSetPosIntentId from 'actions/pos/feSetPosIntentId.action';
import determineAndStorePaymentIntentId from 'bl/pos/determineAndStorePaymentIntentId.bl';
import DialogMessage from 'components/dialog/DialogMessage.component';
import Button from 'components/form/Button.component';
import DepositSpinner from 'components/generic/DepositSpinner.component';
import PosReaderDialog from 'components/pos/PosReaderDialog.component';
import PRDS from 'components/pos/PosReaderDialogStatus.const';
import PosReaderPriceLine from 'components/pos/PosReaderPriceLine.component';
import PosStateMessage from 'components/pos/PosStateMessage.component';
import CurrencySign from 'components/price/CurrencySign.component';
import DEPOSIT_SOURCE from 'constants/payment/depositSource.const';
import PAYMENT_ORIGIN_POS from 'constants/payment/paymentOriginPos.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCheckoutPos } from 'selectors/checkout.selectors';
import { selectCurrentSalonId, selectCurrentSalonPosFlow } from 'selectors/currentSalon.selectors';
import fromAnyToCoinNum from 'utils/converters/any/fromAnyToCoinNum.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import BackButton from 'booking/components/common/bw-header/BackButton';
import HeaderWrapper from 'booking/components/common/bw-header/HeaderWrapper';
import BWContent from 'booking/components/common/BWContent';
import PageTitle from 'booking/components/common/PageTitle';
import PayPopUpPriceLine from 'checkout/components/PayPopUpPriceLine.component';
import RadioFormContainer from 'onboarding/common/RadioForm';

import CN from './AddCreditOnAccount.module.scss';

const MARGIN_TOP_STYLE = Object.freeze({ marginTop: '-196px' });

const STYLE_ERROR = Object.freeze({
  color: '#D32F2F',
  fontSize: '14px',
  textAlign: 'left',
  display: 'block',
  paddingTop: '8px',
  fontWeight: '600',
  marginBottom: '20px',
});

const L = logger('AddCreditOnAccount');

const STYLE_QUICK_FIX = Object.freeze({ 'margin-top': '128px' });

const AddCreditOnAccount = ({
  beInitReaderPayment,
  clientId,
  createDepositBackEnd,
  feSetPosIntentId,
  fetchClientDepositDetails,
  goBackClientProfile,
  pos,
  posFlow,
  salonId,
}) => {
  const [creditAmount, setCreditAmount] = useStateWhileMounted('');
  const [creditErrorMessage, setCreditErrorMessage] = useStateWhileMounted('');
  const [errorSourceMsg, setErrorSourceMsg] = useStateWhileMounted('');
  const [sourceType, setSourceType] = useStateWhileMounted('');
  const [posStep, setPosStep] = useStateWhileMounted(null);
  const { t } = useTranslation();
  const { val: isCreditLoaderVisible, yay: showLoader, nay: hideLoader } = useTip(false);
  const { val: isConfirmDisabled, yay: disableConfirm, nay: enableConfirm } = useTip(true);
  const { val: isPosDialogRendered, yay: showPosDialog, nay: hidePosDialog } = useTip(false);

  const switchRadio = useCallback(
    (targetId) => {
      setErrorSourceMsg('');
      setSourceType(targetId);
    },
    [setErrorSourceMsg, setSourceType],
  );

  const handleSubmit = useCallback(async () => {
    if (!fromAnyToBool(sourceType)) {
      setErrorSourceMsg(t('composite.clientProfile.addCredit.selectPayment'));
    } else if (!fromAnyToBool(creditAmount)) {
      setErrorSourceMsg(t('composite.clientProfile.addCredit.addAmount'));
    } else if (fromAnyToBool(creditAmount) && parseFloat(creditAmount) > 0) {
      if (
        posFlow && // FIXME: the check should be specifically true === posFLow, not any other value
        DEPOSIT_SOURCE.pos === sourceType
      ) {
        showPosDialog();
        enableConfirm();
        hideLoader();
        return;
      }

      try {
        showLoader();

        const result = await createDepositBackEnd(
          {
            amount: fromAnyToCoinNum(creditAmount),
            depositType: 'deposited',
            notes: '',
            clientId,
            used_for_appointmentIds: null,
            source: sourceType,
          },
          salonId,
        );

        if (result.is.ok) {
          const depositResult = await fetchClientDepositDetails(salonId, clientId);

          if (depositResult.is.ok) {
            setCreditAmount('');
            setCreditErrorMessage('');
            setErrorSourceMsg('');
            setSourceType('');
            goBackClientProfile();
          }
        }

        hideLoader();
      } catch (error) {
        L.warn('handleSubmit error', error);
        setErrorSourceMsg(error.message);
      }
    }
  }, [
    sourceType,
    creditAmount,
    setErrorSourceMsg,
    t,
    posFlow,
    showPosDialog,
    enableConfirm,
    hideLoader,
    showLoader,
    createDepositBackEnd,
    clientId,
    salonId,
    fetchClientDepositDetails,
    setCreditAmount,
    setCreditErrorMessage,
    setSourceType,
    goBackClientProfile,
  ]);

  const validateDeposit = useCallback(
    (value) => {
      setCreditAmount(value);
      setCreditErrorMessage('');
    },
    [setCreditAmount, setCreditErrorMessage],
  );

  const confirm = useCallback(
    async (paymentIntentId) => {
      const FTAG = 'confirm()';

      try {
        const result = await createDepositBackEnd(
          {
            amount: fromAnyToCoinNum(creditAmount),
            depositType: 'deposited',
            notes: '',
            clientId,
            used_for_appointmentIds: null,
            source: sourceType,
            intent_id: paymentIntentId,
          },
          salonId,
        );

        if (result.is.ok) {
          const depositResult = await fetchClientDepositDetails(salonId, clientId);

          if (depositResult.is.ok) {
            hideLoader();
            disableConfirm();
            setCreditAmount('');
            setCreditErrorMessage('');
            setErrorSourceMsg('');
            setSourceType('');
            goBackClientProfile();
          }
        }
      } catch (e) {
        L.warn(FTAG, 'confirm error:', e);
        enableConfirm();
        hideLoader();
      }
    },
    [
      clientId,
      createDepositBackEnd,
      creditAmount,
      disableConfirm,
      enableConfirm,
      fetchClientDepositDetails,
      goBackClientProfile,
      hideLoader,
      salonId,
      setCreditAmount,
      setCreditErrorMessage,
      setErrorSourceMsg,
      setSourceType,
      sourceType,
    ],
  );

  const closePopup = useCallback(() => {
    setPosStep(PRDS.closed);
    disableConfirm();
    hidePosDialog();
    hideLoader();
  }, [disableConfirm, hidePosDialog, hideLoader, setPosStep]);

  const handlePosReaderChange = useCallback(
    (ev) => {
      if (ev?.status === PRDS.success) {
        showLoader();
        disableConfirm();
      }
      setPosStep(ev?.status);
    },
    [disableConfirm, setPosStep, showLoader],
  );

  const handlePosReaderAction = useCallback(
    (options) =>
      determineAndStorePaymentIntentId({
        readerId: asObject(options).readerId,
        pos,
        clientId,
        salonId,
        items: [
          {
            description: t('composite.clientProfile.addCredit.paymentDescription'),
            amount: fromAnyToCoinNum(creditAmount),
            price: fromAnyToCoinNum(creditAmount),
            quantity: 1,
          },
        ],
        paymentOrigin: PAYMENT_ORIGIN_POS.credit,
        amount: creditAmount,
        beInitReaderPayment,
        feSetPosIntentId,
      }),
    [pos, t, clientId, salonId, creditAmount, beInitReaderPayment, feSetPosIntentId],
  );

  const handlePosReaderDone = useCallback(
    (options) => {
      const { canceled, success, paymentIntentId: idFromOptions } = asObject(options);

      if (success) {
        L.unignore('handlePosReaderDone()', 'confirm:', confirm(idFromOptions));
      }

      if (canceled) {
        closePopup();
      }
    },
    [closePopup, confirm],
  );

  const handleConfirm = useCallback(() => {
    const isPosTransaction = posFlow === true; // NOTE: make sure POS is used only if posFLow is exactly true

    if (!isPosTransaction || posStep === PRDS.success || posStep === PRDS.continue) {
      return void L.unignore('handleConfirm()', 'confirm:', confirm());
    }

    if (!posStep || posStep === PRDS.closed) {
      return void setPosStep(PRDS.open);
    }

    return void 1;
  }, [confirm, posFlow, posStep, setPosStep]);

  const cardSource = posFlow // FIXME: the check should be specifically true === posFLow, not any other value
    ? DEPOSIT_SOURCE.pos
    : DEPOSIT_SOURCE.card;
  const isPosDialogOpen = posFlow === true && posStep && posStep !== PRDS.closed && posStep !== PRDS.cancel;
  const isPaymentSourceValid = [DEPOSIT_SOURCE.cash, cardSource, DEPOSIT_SOURCE.other].includes(sourceType);

  return (
    <div data-bem="AddCreditOnAccount" className="BookingWindow header-center deposit-booking">
      {isPosDialogRendered && (
        <PosReaderDialog
          open={isPosDialogOpen}
          onChange={handlePosReaderChange}
          onAction={handlePosReaderAction}
          onDone={handlePosReaderDone}
        >
          <DialogMessage>
            <PosStateMessage posStep={posStep} price={creditAmount} />
          </DialogMessage>

          <PosReaderPriceLine description={t('composite.clientProfile.addCredit.total')} total={creditAmount} />
        </PosReaderDialog>
      )}

      <HeaderWrapper>
        <BackButton onClick={() => void goBackClientProfile()} />
        <PageTitle pageTitle={t('composite.clientProfile.addCredit.title')} subTitle="" />
      </HeaderWrapper>

      <BWContent tag="AddCreditOnAccount__content" additionalClass="space">
        <div data-bem="AddCreditOnAccount__amount" className="create-deposite-container cfix" style={STYLE_QUICK_FIX}>
          <small>{t('composite.clientProfile.addCredit.amountLabel')}</small>

          <div className="form cfix">
            <label className="label" htmlFor="enter-deposite">
              <input
                id="enter-deposite"
                className="input-small input-default"
                type="number"
                pattern="[0-9]*"
                onChange={(ev) => void validateDeposit(ev.target.value)}
                value={creditAmount}
                placeholder={t('composite.clientProfile.addCredit.amountPlaceholder')}
              />
            </label>
          </div>

          <div data-bem="AddCreditOnAccount__how" className="form cfix cash-or-card">
            <small>{t('composite.clientProfile.addCredit.payingTitle')}</small>
            <div className="row radio-group  cb-group-payment add-credit-radio-group">
              <RadioFormContainer
                selections={[
                  {
                    id: 'cash',
                    label: t('composite.clientProfile.addCredit.option1'),
                    name: 'payment',
                    checked: sourceType === DEPOSIT_SOURCE.cash,
                  },
                  {
                    id: cardSource,
                    label: t('composite.clientProfile.addCredit.option2'),
                    name: 'payment',
                    checked: sourceType === cardSource,
                  },
                  {
                    id: 'other',
                    label: t('composite.clientProfile.addCredit.option3'),
                    name: 'payment',
                    checked: sourceType === DEPOSIT_SOURCE.other,
                  },
                ]}
                onChange={(e) => {
                  const sourceValue = e ? e.target.id : '';
                  switchRadio(sourceValue);
                }}
              />
            </div>
          </div>

          {creditErrorMessage !== '' && (
            <div>
              <span style={STYLE_ERROR}>{creditErrorMessage}</span>
            </div>
          )}

          {errorSourceMsg !== '' && (
            <div>
              <span style={STYLE_ERROR}>{errorSourceMsg}</span>
            </div>
          )}
          <div className={CN.buttonContainer}>
            <Button
              variant="primaryForce"
              xSpace
              onClick={handleSubmit}
              disabled={!sourceType || !isPaymentSourceValid || !creditAmount}
            >
              {t('composite.clientProfile.addCredit.saveCreditButton')}
            </Button>
          </div>
        </div>
      </BWContent>
      {isCreditLoaderVisible && (
        <DepositSpinner color="#3e0b6c" message={t('composite.clientProfile.addCredit.addingCredit')} />
      )}

      {!isConfirmDisabled && !isCreditLoaderVisible && (
        <div
          data-bem="AddCreditOnAccount__POS-bill"
          className="chk-popup animated zoomIn pay-by-modal"
          style={MARGIN_TOP_STYLE}
        >
          <div data-bem="AddCreditOnAccount__POS-title" className="chk-popup-header">
            <h2>
              {t('composite.clientProfile.addCredit.confirmPayment')} <CurrencySign />
              {fromAnyToCoinNum(creditAmount)}
            </h2>
          </div>

          <div data-bem="AddCreditOnAccount__POS-body" className="chk-popup-wrap">
            {creditAmount && (
              <PayPopUpPriceLine
                description={t('composite.clientProfile.addCredit.credit')}
                value={fromAnyToNumber(creditAmount)}
              />
            )}
          </div>

          <div data-bem="AddCreditOnAccount__POS-buttons" className="chk-popup-footer cfix">
            <button
              data-bem="AddCreditOnAccount__POS-cancel-button"
              className="btn btn-violet-link"
              onClick={closePopup}
            >
              {t('composite.clientProfile.addCredit.cancelButton')}
            </button>

            <button
              data-bem="AddCreditOnAccount__POS-confirm-button"
              id="confirm"
              className="btn btn-violet"
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
            >
              {t('composite.clientProfile.addCredit.confirmButton')}
            </button>
          </div>
        </div>
      )}

      {!isConfirmDisabled && !isCreditLoaderVisible && <div className="bg" />}
    </div>
  );
};

const AddCreditOnAccountHoc = connect(
  (state) => ({
    pos: selectCheckoutPos(state),
    posFlow: selectCurrentSalonPosFlow(state),
    salonId: selectCurrentSalonId(state),
  }),
  (dispatch) =>
    bindActionCreators(
      {
        beInitReaderPayment,
        feSetPosIntentId,
      },
      dispatch,
    ),
)(AddCreditOnAccount);

export default AddCreditOnAccountHoc;
